import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const SALARY_MIN_COLOR = { r: 239, g: 246, b: 255 };
const SALARY_MAX_COLOR = { r: 29, g: 78, b: 216 };
const SORTABLE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'position', label: 'Position' },
  { key: 'hireDate', label: 'Hire Date' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'salary', label: 'Salary' }
];

const Team = () => {
  const navigate = useNavigate();
  const [hiredLeads, setHiredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'startDate', direction: 'desc' });

  useEffect(() => {
    const fetchHiredLeads = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/applicants', {
          params: {
            status: 'hired',
            page: 1,
            limit: 100,
            sortBy: 'startDate',
            sortOrder: 'DESC'
          }
        });

        setHiredLeads(response.data.applicants || []);
      } catch (error) {
        console.error('Error fetching hired leads:', error);
        setHiredLeads([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHiredLeads();
  }, []);

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleDateString();
  };

  const salaryValues = hiredLeads
    .map((lead) => Number(lead.salary))
    .filter((salary) => Number.isFinite(salary) && salary > 0);

  const minSalary = salaryValues.length > 0 ? Math.min(...salaryValues) : 0;
  const maxSalary = salaryValues.length > 0 ? Math.max(...salaryValues) : 0;

  const getSalaryBadgeStyle = (salaryValue) => {
    const salary = Number(salaryValue);

    if (!Number.isFinite(salary) || salary <= 0) {
      return null;
    }

    const range = maxSalary - minSalary;
    const ratio = range === 0 ? 1 : (salary - minSalary) / range;
    const red = Math.round(SALARY_MIN_COLOR.r + (SALARY_MAX_COLOR.r - SALARY_MIN_COLOR.r) * ratio);
    const green = Math.round(SALARY_MIN_COLOR.g + (SALARY_MAX_COLOR.g - SALARY_MIN_COLOR.g) * ratio);
    const blue = Math.round(SALARY_MIN_COLOR.b + (SALARY_MAX_COLOR.b - SALARY_MIN_COLOR.b) * ratio);
    const textColor = ratio > 0.55 ? 'rgb(255, 255, 255)' : 'rgb(30, 41, 59)';

    return {
      backgroundColor: `rgb(${red}, ${green}, ${blue})`,
      color: textColor
    };
  };

  const sortedHiredLeads = useMemo(() => {
    const leads = [...hiredLeads];
    const { key, direction } = sortConfig;
    const sortMultiplier = direction === 'asc' ? 1 : -1;

    const getSortValue = (lead) => {
      if (key === 'salary') {
        const salary = Number(lead.salary);
        return Number.isFinite(salary) ? salary : null;
      }

      if (key === 'hireDate' || key === 'startDate') {
        const timestamp = lead[key] ? new Date(lead[key]).getTime() : null;
        return Number.isFinite(timestamp) ? timestamp : null;
      }

      return (lead[key] || '').toString().toLowerCase();
    };

    leads.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return 1;
      if (valueB == null) return -1;
      if (valueA < valueB) return -1 * sortMultiplier;
      if (valueA > valueB) return 1 * sortMultiplier;
      return 0;
    });

    return leads;
  }, [hiredLeads, sortConfig]);

  const toggleSort = (columnKey) => {
    setSortConfig((current) => {
      if (current.key === columnKey) {
        return {
          key: columnKey,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return {
        key: columnKey,
        direction: columnKey === 'startDate' ? 'desc' : 'asc'
      };
    });
  };

  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕';
    }

    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <div className="overflow-hidden rounded-lg bg-white shadow">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading hired leads...</div>
          ) : hiredLeads.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No hired leads found.</div>
          ) : (
            <div>
              <div className="hidden border-b border-gray-200 bg-gray-50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 sm:grid sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(120px,1fr)] sm:gap-4">
                {SORTABLE_COLUMNS.map((column) => (
                  <button
                    key={column.key}
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className="flex items-center gap-1 text-left transition hover:text-gray-700"
                  >
                    <span>{column.label}</span>
                    <span className="text-[10px]">{getSortIndicator(column.key)}</span>
                  </button>
                ))}
              </div>
              <ul className="divide-y divide-gray-200">
              {sortedHiredLeads.map((lead) => (
                <li key={lead.id} className="px-6 py-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/applicants/${lead.id}`)}
                    className="grid w-full grid-cols-1 gap-2 text-left text-sm text-gray-700 hover:text-gray-900 sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(120px,1fr)] sm:gap-4"
                  >
                    <span className="font-semibold text-blue-600 hover:underline">{lead.name}</span>
                    <span>{lead.position || '-'}</span>
                    <span>{formatDate(lead.hireDate)}</span>
                    <span>{formatDate(lead.startDate)}</span>
                    <span>
                      {lead.salary ? (
                        <span
                          className="inline-flex min-w-[96px] justify-end rounded-md px-2.5 py-1 font-medium"
                          style={getSalaryBadgeStyle(lead.salary)}
                        >
                          {Number(lead.salary).toLocaleString()}
                        </span>
                      ) : (
                        '-'
                      )}
                    </span>
                  </button>
                </li>
              ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Team;
